import type { Vec3 } from '../core/Vec3';

/**
 * Contact-continuity rotation (Phase 3B). `velocity` slides along plane `p`
 * and meets plane `n` (both unit normals, at most a small seam angle apart).
 * The part of `velocity` tangent to `p` is rotated by the rotation taking `p`
 * to `n` (Rodrigues, about k = p × n), so it keeps its speed and becomes tangent
 * to `n`. The part along `p` is dropped if it points into `p` and kept if it
 * separates. The result is written to `out` and never exceeds |velocity|.
 *
 * Returns false (leaving `out` untouched) when p and n are parallel.
 */
export function carryAcrossSeam(velocity: Readonly<Vec3>, p: Readonly<Vec3>, n: Readonly<Vec3>, out: Vec3, scratch: { t: Vec3; k: Vec3 }): boolean {
  const k = scratch.k.crossVectors(p, n);
  const sinRaw = k.length();
  if (sinRaw < 1e-9) return false;
  k.scale(1 / sinRaw);
  // Taking the angle from atan2 keeps (c, s) on the unit circle even when the
  // normals are not exactly unit length, so the rotation preserves length.
  const angle = Math.atan2(sinRaw, p.dot(n));
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const speed = velocity.length();
  const into = velocity.dot(p);
  const t = scratch.t.copy(velocity).addScaled(p, -into);
  const kt = k.dot(t);
  out.set(
    t.x * c + (k.y * t.z - k.z * t.y) * s + k.x * kt * (1 - c),
    t.y * c + (k.z * t.x - k.x * t.z) * s + k.y * kt * (1 - c),
    t.z * c + (k.x * t.y - k.y * t.x) * s + k.z * kt * (1 - c),
  );
  if (into > 0) {
    // The rotated tangent is no longer perpendicular to p, so re-adding the
    // separating part could exceed the original speed: bound it.
    out.addScaled(p, into);
    const after = out.length();
    if (after > speed) out.scale(speed / after);
  }
  return true;
}
